import { useState, useEffect } from 'react';
import { useToast } from '../hooks/useToast';
import './ManualDeposit.css';

function ManualDeposit({ userId, minimumDeposit, onBack }) {
  const [amount, setAmount] = useState('');
  const [utrNo, setUtrNo] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = useState(true);
  const toast = useToast();

  useEffect(() => {
    fetchPaymentDetails();
  }, []);

  const fetchPaymentDetails = async () => {
    try {
      const response = await fetch(
        'https://backend.mainbazar777.site/api/fetch-amount-details',
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setPaymentDetails(data);
      setIsLoadingPaymentDetails(false);
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setIsLoadingPaymentDetails(false);
    }
  };


  const handleCopyUpiId = async () => {
    if (!paymentDetails?.upi_id) return;
    try {
      await navigator.clipboard.writeText(paymentDetails.upi_id);
      toast.success('UPI ID copied');
    } catch (err) {
      toast.error('Failed to copy UPI ID');
    }
  };

  const validateForm = () => {
    if (!amount || Number(amount) === 0) {
      setError('Please enter an amount');
      return false;
    }

    if (Number(amount) < minimumDeposit) {
      setError(`Minimum deposit is ₹${minimumDeposit}`);
      return false;
    }

    if (!utrNo) {
      setError('Please enter UTR number');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        'https://backend.mainbazar777.site/api/add-amount',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            amount: amount,
            utr_no: utrNo,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success || data.message?.includes('successfully')) {
        toast.success('Deposit request submitted successfully!');
        setTimeout(() => {
          onBack?.();
        }, 2000);
      } else {
        throw new Error(data.message || 'Failed to submit deposit request');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      toast.error(err.message || 'Failed to submit deposit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="manual-deposit-container">
      <div className="deposit-form">
        <h2>Add Funds</h2>

        {isLoadingPaymentDetails ? (
          <div className="loading-state">
            <p>Loading payment details...</p>
          </div>
        ) : (
          <>
            {error && <div className="error-alert">{error}</div>}

            {/* Payment Apps Section */}
            <div className="payment-apps-section">
              <p className="payment-apps-title">
                Open Paytm or PhonePe app or any payment app to make instant deposit using QR or UPI link.
              </p>
              <p className="payment-apps-subtitle">
                (फास्ट डिपॉज़िट के लिए Paytm या PhonePe ऐप डाउनलोड करें और नीचे दिए गए लिंक से पेमेंट करें।)
              </p>
              <div className="payment-apps-logos">
                {/* Paytm Logo */}
                <div className="logo-container">
                  <div className="logo-wrapper">
                    <svg version="1.1" viewBox="0 0 122.88 38.52" xmlns="http://www.w3.org/2000/svg">
                      <style type="text/css">
                        {`.st0{fill:#20336B;} .st1{fill:#00BAF2;}`}
                      </style>
                      <g>
                        <path className="st1" d="M122.47,11.36c-1.12-3.19-4.16-5.48-7.72-5.48h-0.08c-2.32,0-4.41,0.97-5.9,2.52 c-1.49-1.55-3.58-2.52-5.9-2.52h-0.07c-2.04,0-3.91,0.75-5.34,1.98V7.24c-0.05-0.63-0.56-1.12-1.2-1.12h-5.48 c-0.67,0-1.21,0.54-1.21,1.21v29.74c0,0.67,0.54,1.21,1.21,1.21h5.48c0.61,0,1.12-0.46,1.19-1.04l0-21.35c0-0.08,0-0.14,0.01-0.21 c0.09-0.95,0.79-1.74,1.89-1.83h1.01c0.46,0.04,0.85,0.2,1.15,0.45c0.48,0.38,0.74,0.96,0.74,1.6l0.02,21.24 c0,0.67,0.54,1.22,1.21,1.22h5.48c0.65,0,1.17-0.51,1.2-1.15l0-21.33c0-0.7,0.32-1.34,0.89-1.71c0.28-0.18,0.62-0.3,1.01-0.34h1.01 c1.19,0.1,1.9,1,1.9,2.05l0.02,21.22c0,0.67,0.54,1.21,1.21,1.21h5.48c0.64,0,1.17-0.5,1.21-1.13V13.91 C122.86,12.6,122.69,11.99,122.47,11.36L122.47,11.36z M85.39,6.2h-3.13V1.12c0-0.01,0-0.01,0-0.02C82.26,0.5,81.77,0,81.15,0 c-0.07,0-0.14,0.01-0.21,0.02c-3.47,0.95-2.78,5.76-9.12,6.17h-0.61c-0.09,0-0.18,0.01-0.27,0.03h-0.01l0.01,0 C70.41,6.35,70,6.83,70,7.41v5.48c0,0.67,0.54,1.21,1.21,1.21h3.3l-0.01,23.22c0,0.66,0.54,1.2,1.2,1.2h5.42 c0.66,0,1.2-0.54,1.2-1.2l0-23.22h3.07c0.66,0,1.21-0.55,1.21-1.21V7.41C86.6,6.74,86.06,6.2,85.39,6.2L85.39,6.2z"/>
                        <path className="st0" d="M65.69,6.2h-5.48C59.55,6.2,59,6.74,59,7.41v11.33c-0.01,0.7-0.58,1.26-1.28,1.26h-2.29 c-0.71,0-1.29-0.57-1.29-1.28L54.12,7.41c0-0.67-0.54-1.21-1.21-1.21h-5.48c-0.67,0-1.21,0.54-1.21,1.21v12.41 c0,4.71,3.36,8.08,8.08,8.08c0,0,3.54,0,3.65,0.02c0.64,0.07,1.13,0.61,1.13,1.27c0,0.65-0.48,1.19-1.12,1.27 c-0.03,0-0.06,0.01-0.09,0.02l-8.01,0.03c-0.67,0-1.21,0.54-1.21,1.21v5.47c0,0.67,0.54,1.21,1.21,1.21h8.95 c4.72,0,8.08-3.36,8.08-8.07V7.41C66.9,6.74,66.36,6.2,65.69,6.2L65.69,6.2z M34.53,6.23h-7.6c-0.67,0-1.22,0.51-1.22,1.13v2.13 c0,0.01,0,0.03,0,0.04c0,0.02,0,0.03,0,0.05v2.92c0,0.66,0.58,1.21,1.29,1.21h7.24c0.57,0.09,1.02,0.51,1.09,1.16v0.71 c-0.06,0.62-0.51,1.07-1.06,1.12h-3.58c-4.77,0-8.16,3.17-8.16,7.61v6.37c0,4.42,2.92,7.56,7.65,7.56h9.93 c1.78,0,3.23-1.35,3.23-3.01V14.45C43.34,9.41,40.74,6.23,34.53,6.23L34.53,6.23z M35.4,29.09v0.86c0,0.07-0.01,0.14-0.02,0.2 c-0.01,0.06-0.03,0.12-0.05,0.18c-0.17,0.48-0.65,0.83-1.22,0.83h-2.28c-0.71,0-1.29-0.54-1.29-1.21v-1.03c0-0.01,0-0.03,0-0.04 l0-2.75v-0.86l0-0.01c0-0.66,0.58-1.2,1.29-1.2h2.28c0.71,0,1.29,0.54,1.29,1.21V29.09L35.4,29.09z M13.16,6.19H1.19 C0.53,6.19,0,6.73,0,7.38v5.37c0,0.01,0,0.02,0,0.03c0,0.03,0,0.05,0,0.07v24.29c0,0.66,0.49,1.2,1.11,1.21h5.58 c0.67,0,1.21-0.54,1.21-1.21l0.02-8.32h5.24c4.38,0,7.44-3.04,7.44-7.45v-7.72C20.6,9.25,17.54,6.19,13.16,6.19L13.16,6.19z M12.68,16.23v3.38c0,0.71-0.57,1.29-1.28,1.29l-3.47,0v-6.77h3.47c0.71,0,1.28,0.57,1.28,1.28V16.23L12.68,16.23z"/>
                      </g>
                    </svg>
                    <p className="logo-label" style={{'color':'black'}}>Paytm</p>
                  </div>
                </div>

                {/* PhonePe Logo */}
                <div className="logo-container">
                  <div className="logo-wrapper">
                    <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2">
                      <circle cx="-25.926" cy="41.954" r="29.873" fill="#5f259f" transform="rotate(-76.714 -48.435 5.641) scale(8.56802)"/>
                      <path d="M372.164 189.203c0-10.008-8.576-18.593-18.584-18.593h-34.323l-78.638-90.084c-7.154-8.577-18.592-11.439-30.03-8.577l-27.17 8.577c-4.292 1.43-5.723 7.154-2.862 10.007l85.8 81.508H136.236c-4.293 0-7.154 2.861-7.154 7.154v14.292c0 10.016 8.585 18.592 18.592 18.592h20.015v68.639c0 51.476 27.17 81.499 72.931 81.499 14.292 0 25.739-1.431 40.03-7.146v45.753c0 12.87 10.016 22.886 22.885 22.886h20.015c4.293 0 8.577-4.293 8.577-8.586V210.648h32.893c4.292 0 7.145-2.861 7.145-7.145v-14.3zM280.65 312.17c-8.576 4.292-20.015 5.723-28.591 5.723-22.886 0-34.324-11.438-34.324-37.176v-68.639h62.915v100.092z" fill="#fff" fillRule="nonzero"/>
                    </svg>
                    <p className="logo-label" style={{'color':'black'}}>Phone Pe</p>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code & UPI ID Section */}
            {paymentDetails && (
              <div className="payment-details-box">
                {paymentDetails.qr && (
                  <div className="qr-section">
                    <p className="section-label">Scan QR to Pay</p>
                    <img
                      src={`https://backend.mainbazar777.site/${paymentDetails.qr}`}
                      alt="Payment QR Code"
                      className="qr-code"
                    />
                  </div>
                )}

                {paymentDetails.upi_id && (
                  <div className="upi-section">
                    <p className="section-label">Or Pay to UPI ID</p>
                    <div className="upi-id-box">
                      <code>{paymentDetails.upi_id}</code>
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={handleCopyUpiId}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Amount Field */}
            <div className="form-group">
              <label htmlFor="amount">Amount (₹)</label>
              <input
                id="amount"
                type="number"
                className="form-input"
                placeholder="Enter amount"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setError('');
                }}
                min={minimumDeposit}
                disabled={isSubmitting}
              />
              <small className="input-hint">Minimum: ₹{minimumDeposit}</small>
            </div>
            {/* UTR Number Field */}
            <div className="form-group">
              <label htmlFor="utrNo">Enter UTR Number</label>
              <p className="section-label" style={{ marginBottom: '8px', fontSize: '12px', color: '#666' }}>
                After completing payment, enter your UTR number below
              </p>
              <input
                id="utrNo"
                type="text"
                className="form-input"
                placeholder="e.g., 202605021234567"
                value={utrNo}
                onChange={(e) => {
                  setUtrNo(e.target.value);
                  setError('');
                }}
                disabled={isSubmitting}
              />
              <small className="input-hint">Your transaction reference number</small>
            </div>

            {/* Info Box */}
            <div className="info-box">
              <p>✓ Secure UPI Payment</p>
              <p>✓ Instant transfer</p>
              <p>✓ 100% safe process</p>
            </div>

            {/* Submit Button */}
            <button
              className="submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Deposit Request'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ManualDeposit;
